import { connect } from "react-redux";
import styles from "./index.module.css";
import { Box, Container, IconButton, Modal, Typography } from "@mui/material";
import MaterialSelect from "../../components/MaterialSelect/index.jsx";
import { useContext, useEffect, useMemo, useState } from "react";
import {
  getListTokens,
  refreshToken,
} from "../../actions/manageSessionsAction.js";
import { Close, Replay } from "@mui/icons-material";
import Table from "../../components/Table/index.jsx";
import { SocketContext } from "../../context/socketContext.js";
import {
  closeLoading,
  openLoading,
} from "../../components/loadingModal/loading.js";
import { getModulesIdsByUser } from "../../actions/productsActions.js";
import { getCourseNames } from "../../actions/manageCoursesActions.js";

const TokensModal = ({
  openModal,
  setOpenModal,
  courses,
  refreshToken,
  getListTokens,
  getModulesIdsByUser,
  getCourseNames,
}) => {
  const { socket } = useContext(SocketContext);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [users, setUsers] = useState([]);
  const [counters, setCounters] = useState({});
  const mediaQuery = window.matchMedia("(max-width: 427px)");

  const getInitialData = async () => {
    openLoading();
    try {
      await getCourseNames({ active: true, userProducts: true });
      await getModulesIdsByUser();
    } catch (error) {
      console.log(error);
      alert("Error", error);
    } finally {
      closeLoading();
    }
  };

  useEffect(() => {
    if (openModal) {
      getInitialData();
    }
  }, [openModal]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCounters((prevCounters) => {
        const updatedCounters = { ...prevCounters };
        let needsUpdate = false;

        // Decrementar todos los contadores activos
        for (const userId in updatedCounters) {
          if (updatedCounters[userId] > 0) {
            updatedCounters[userId] -= 1;
            needsUpdate = true;
          } else if (updatedCounters[userId] === 0) {
            // Cuando el contador llega a 0, regenerar el token
            regenerateToken(userId);
            updatedCounters[userId] = 300; // Reiniciar a 5 minutos (300 segundos)
            needsUpdate = true;
          }
        }

        return needsUpdate ? updatedCounters : prevCounters;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleSelectCourse = async (e) => {
    setSelectedCourse(e.target.value);
    openLoading();
    try {
      await getListTokens({ idCourse: e.target.value });
    } catch (error) {
      alert("Errro", error);
    } finally {
      closeLoading();
    }
  };

  const regenerateToken = async (id, source) => {
    source === "button" && openLoading();
    try {
      await refreshToken({ idUser: id });
      // Iniciar contador para este usuario (5 minutos = 300 segundos)
      setCounters((prev) => ({ ...prev, [id]: 300 }));
    } catch (error) {
      alert("Error", error);
    } finally {
      source === "button" && closeLoading();
    }
  };

  useEffect(() => {
    if (selectedCourse && socket) {
      openLoading();
      socket.on(`web:update-tokens-by-course-${selectedCourse}`, (data) => {
        if (data.user) {
          setUsers((prevUsers) =>
            prevUsers.map((u) =>
              parseInt(u.idUser, 10) === parseInt(data.user.idUser, 10)
                ? { ...u, token: data.user.token }
                : u,
            ),
          );
          // Reiniciar el contador para el usuario actualizado
          setCounters((prevCounters) => ({
            ...prevCounters,
            [data.user.idUser]: 300, // Reiniciar a 5 minutos
          }));
        } else {
          setUsers(data.users);
          // Inicializar contadores para nuevos usuarios
          const initialCounters = {};
          data.users.forEach((user) => {
            initialCounters[user.idUser] = 300; // 5 minutos
          });
          setCounters(initialCounters);
        }
        closeLoading();
      });
    }
    return () => {
      if (socket) {
        socket.off(`web:update-tokens-by-course-${selectedCourse}`);
      }
      setUsers([]);
      setCounters({});
    };
  }, [selectedCourse]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "username",
        header: "Username",
        enableColumnFilter: true,
        enableSorting: false,
        size: 250,
      },
      {
        accessorKey: "firstname",
        header: "First Name",
        enableColumnFilter: true,
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: "lastname",
        header: "Last Name",
        enableColumnFilter: true,
        enableSorting: false,
        size: 150,
      },
      {
        accessorKey: "token",
        header: "Token",
        enableSorting: false,
        enableColumnFilter: false,
        size: 80,
      },

      {
        id: "countdown",
        header: "Time Left",
        Cell: ({ row }) => (
          <Typography
            variant="body2"
            sx={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            {counters[row.original.idUser] !== undefined
              ? formatTime(counters[row.original.idUser])
              : "5:00"}
          </Typography>
        ),
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
        muiTableBodyCellProps: {
          sx: {
            textAlign: "center",
            justifyContent: "center",
          },
        },
      },
      {
        id: "actions",
        header: "Regenerate",
        Cell: ({ row }) => (
          <IconButton
            onClick={() => regenerateToken(row.original.idUser, "button")}
          >
            <Replay />
          </IconButton>
        ),
        size: 50,
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    [users, counters],
  );

  const muiTablePaperProps = {
    elevation: 0,
    sx: {
      border: "none",
    },
    muiTableContainerProps: {
      sx: {
        border: "none",
      },
    },
  };

  return (
    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <div className={styles.modal}>
        <Container
          disableGutters
          maxWidth={false}
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            height: mediaQuery.matches ? "100vh" : "80vh",
          }}
        >
          <Box
            sx={{
              background:
                "linear-gradient(90deg, #d8e4f1 0%, #ebe2f0 40%, #f4f6f9 100%);",
              color: "#876abf",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 20px",
              borderTopLeftRadius: "12px",
              borderTopRightRadius: "12px",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              color="#876abf"
              sx={{
                fontFamily: "ProximaNova-Extrabold",
                fontSize: "2vh",
                letterSpacing: "0.4vh",
              }}
            >
              USER TOKENS
            </Typography>
            <IconButton
              onClick={() => setOpenModal(false)}
              sx={{
                color: "#876abf",
              }}
            >
              <Close />
            </IconButton>
          </Box>
          <div style={{ marginTop: "20px", padding: "20px" }}>
            <div style={{ width: "40%" }}>
              <MaterialSelect
                formField="course"
                label="Select Course"
                value={selectedCourse}
                onChange={handleSelectCourse}
                options={courses?.map((course) => ({
                  label: course.name,
                  id: course.idCourse,
                }))}
                disabled={socket === null}
              />
            </div>

            {selectedCourse && (
              <div>
                {users.length ? (
                  <div style={{ marginTop: "30px", width: "400px80%" }}>
                    <Table
                      data={users}
                      height="50vh"
                      columns={columns}
                      muiTablePaperProps={muiTablePaperProps}
                    />
                  </div>
                ) : (
                  <div style={{ marginTop: "30px" }}>
                    <Typography variant="h6" component="h2">
                      This group has no students
                    </Typography>
                  </div>
                )}
              </div>
            )}
          </div>
        </Container>
      </div>
    </Modal>
  );
};

export default connect(null, {
  refreshToken,
  getListTokens,
  getCourseNames,
  getModulesIdsByUser,
})(TokensModal);
